import { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Typography,
  Paper,
  Switch,
  FormControlLabel,
  Button,
  Alert,
  Divider,
  CircularProgress,
  Card,
  CardContent,
  Grid,
  Chip,
  IconButton,
  Tooltip
} from '@mui/material';
import {
  Refresh as RefreshIcon,
  Visibility as VisibilityIcon,
  VisibilityOff as VisibilityOffIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon
} from '@mui/icons-material';
import { useFeatureFlags } from '../contexts/FeatureFlagContext';
import { useNavigate } from 'react-router-dom';

export default function DemoControlsPage() {
  const navigate = useNavigate();
  const { features, loading, error, toggleFeature, refreshFlags } = useFeatureFlags();
  const [toggleStatus, setToggleStatus] = useState({});
  const [lastUpdate, setLastUpdate] = useState(new Date());

  // Feature definitions with descriptions
  const featureDefinitions = {
    EQUIPMENT_CATEGORIES: {
      name: 'Equipment Categories',
      description: 'Enable categorization and filtering of equipment by type',
      impacts: ['Category filters in equipment list', 'Category selection in forms', 'Category-based reports']
    },
    ENHANCED_SEARCH: {
      name: 'Enhanced Search',
      description: 'Advanced search filters and options',
      impacts: ['Date range filters', 'Warehouse/zone filters', 'Multi-field search']
    },
    ADVANCED_REPORTING: {
      name: 'Advanced Reporting',
      description: 'Analytics, charts, and utilization reports',
      impacts: ['Dashboard charts', 'Utilization metrics', 'ROI analysis', 'Trend reports']
    },
    JOB_EQUIPMENT_ASSIGNMENT: {
      name: 'PO Assignment Workflow',
      description: 'Enable direct PO assignment and job equipment workflow features',
      impacts: ['PO Assignment Scanner in Receive tab', 'Barcode → PO assignment', 'Job equipment tracking', 'Stack management']
    },
    EQUIPMENT_PLANNING_MODULE: {
      name: 'Job Equipment Planning',
      description: 'Enable comprehensive job equipment planning and management features',
      impacts: ['Equipment planning interface', 'Job equipment requirements', 'Order management', 'Equipment shortage reports']
    },
    QUICK_STACK_SCANNING: {
      name: 'Quick Stack Scanning',
      description: 'Enable quick stack scanning and job stack management features',
      impacts: ['Stack quantity updates', 'Barcode scanning for stacks', 'Job stack dashboard', 'Quick equipment movement']
    }
  };

  // Handle feature toggle
  const handleToggle = async (featureName) => {
    setToggleStatus({ ...toggleStatus, [featureName]: 'loading' });
    
    const newValue = !features[featureName]?.enabled;
    const result = await toggleFeature(featureName, newValue);
    
    if (result?.success) {
      setToggleStatus({ ...toggleStatus, [featureName]: 'success' });
      setLastUpdate(new Date());
      
      // Clear status after 2 seconds
      setTimeout(() => {
        setToggleStatus(prev => ({ ...prev, [featureName]: null }));
      }, 2000);
    } else {
      setToggleStatus({ ...toggleStatus, [featureName]: 'error' });
    }
  };

  // Preset configurations
  const applyPreset = async (preset) => {
    const presets = {
      mvp: { 
        EQUIPMENT_CATEGORIES: false, 
        ENHANCED_SEARCH: false, 
        ADVANCED_REPORTING: false,
        JOB_EQUIPMENT_ASSIGNMENT: false,
        EQUIPMENT_PLANNING_MODULE: false,
        QUICK_STACK_SCANNING: false
      },
      standard: { 
        EQUIPMENT_CATEGORIES: true, 
        ENHANCED_SEARCH: false, 
        ADVANCED_REPORTING: false,
        JOB_EQUIPMENT_ASSIGNMENT: true,
        EQUIPMENT_PLANNING_MODULE: false,
        QUICK_STACK_SCANNING: true
      },
      professional: { 
        EQUIPMENT_CATEGORIES: true, 
        ENHANCED_SEARCH: true, 
        ADVANCED_REPORTING: true,
        JOB_EQUIPMENT_ASSIGNMENT: true,
        EQUIPMENT_PLANNING_MODULE: true,
        QUICK_STACK_SCANNING: true
      }
    };
    
    const config = presets[preset];
    for (const [feature, enabled] of Object.entries(config)) {
      if (features[feature]?.enabled !== enabled) {
        await toggleFeature(feature, enabled);
      }
    }
  };

  // Auto-refresh every 5 seconds
  useEffect(() => {
    const interval = setInterval(() => {
      setLastUpdate(new Date());
    }, 5000);
    return () => clearInterval(interval);
  }, []);

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="100vh">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Paper elevation={3} sx={{ p: 4 }}>
        {/* Header */}
        <Box display="flex" alignItems="center" justifyContent="space-between" mb={3}>
          <Typography variant="h4" component="h1">
            Demo Control Panel
          </Typography>
          <Box display="flex" gap={2}>
            <Tooltip title="Refresh">
              <IconButton onClick={refreshFlags}>
                <RefreshIcon />
              </IconButton>
            </Tooltip>
            <Typography variant="caption" color="text.secondary" sx={{ alignSelf: 'center' }}>
              Last updated: {lastUpdate.toLocaleTimeString()}
            </Typography>
          </Box>
        </Box>

        {/* Warning */}
        <Alert severity="warning" sx={{ mb: 3 }}>
          <Typography variant="body2" fontWeight="bold">
            🔒 Admin Only - Customer Cannot See This Page
          </Typography>
          <Typography variant="body2">
            This control panel is for demo purposes only. Changes affect the entire staging environment immediately.
          </Typography>
        </Alert>

        {/* Demo Instructions */}
        <Card sx={{ mb: 3, bgcolor: 'grey.50' }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              📋 Demo Flow Instructions
            </Typography>
            <ol style={{ margin: 0, paddingLeft: 20 }}>
              <li>Start with MVP Mode (all features OFF)</li>
              <li>Show basic functionality to establish value</li>
              <li>Toggle features ON during demo to show progression</li>
              <li>Customer sees features appear in real-time</li>
              <li>Use presets for quick configuration changes</li>
            </ol>
          </CardContent>
        </Card>

        {/* Preset Buttons */}
        <Box mb={4}>
          <Typography variant="h6" gutterBottom>
            Quick Presets
          </Typography>
          <Grid container spacing={2}>
            <Grid item>
              <Button 
                variant="outlined" 
                onClick={() => applyPreset('mvp')}
                startIcon={<VisibilityOffIcon />}
              >
                MVP Mode
              </Button>
            </Grid>
            <Grid item>
              <Button 
                variant="outlined" 
                onClick={() => applyPreset('standard')}
                color="info"
              >
                Standard Mode
              </Button>
            </Grid>
            <Grid item>
              <Button 
                variant="outlined" 
                onClick={() => applyPreset('professional')}
                startIcon={<VisibilityIcon />}
                color="success"
              >
                Professional Mode
              </Button>
            </Grid>
          </Grid>
        </Box>

        <Divider sx={{ my: 3 }} />

        {/* Feature Toggles */}
        <Typography variant="h6" gutterBottom>
          Feature Controls
        </Typography>
        
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>
        )}

        <Grid container spacing={3}>
          {Object.entries(featureDefinitions).map(([featureKey, featureInfo]) => {
            const isEnabled = features[featureKey]?.enabled || false;
            const status = toggleStatus[featureKey];
            
            return (
              <Grid item xs={12} key={featureKey}>
                <Card variant="outlined">
                  <CardContent>
                    <Box display="flex" alignItems="center" justifyContent="space-between">
                      <Box flex={1}>
                        <Box display="flex" alignItems="center" gap={1}>
                          <Typography variant="h6">
                            {featureInfo.name}
                          </Typography>
                          {isEnabled && (
                            <Chip 
                              label="ENABLED" 
                              color="success" 
                              size="small"
                              icon={<CheckCircleIcon />}
                            />
                          )}
                          {!isEnabled && (
                            <Chip 
                              label="DISABLED" 
                              size="small"
                              icon={<CancelIcon />}
                            />
                          )}
                        </Box>
                        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                          {featureInfo.description}
                        </Typography>
                        <Box mt={1}>
                          <Typography variant="caption" color="text.secondary">
                            Impacts:
                          </Typography>
                          <ul style={{ margin: 0, paddingLeft: 20 }}>
                            {featureInfo.impacts.map((impact, idx) => (
                              <li key={idx}>
                                <Typography variant="caption">{impact}</Typography>
                              </li>
                            ))}
                          </ul>
                        </Box>
                      </Box>
                      <Box display="flex" alignItems="center" gap={2}>
                        {status === 'loading' && <CircularProgress size={20} />}
                        {status === 'success' && <CheckCircleIcon color="success" />}
                        {status === 'error' && <CancelIcon color="error" />}
                        <FormControlLabel
                          control={
                            <Switch
                              checked={isEnabled}
                              onChange={() => handleToggle(featureKey)}
                              disabled={status === 'loading'}
                              color="primary"
                            />
                          }
                          label={isEnabled ? 'ON' : 'OFF'}
                        />
                      </Box>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            );
          })}
        </Grid>

        {/* Footer */}
        <Box mt={4} pt={3} borderTop={1} borderColor="divider">
          <Typography variant="body2" color="text.secondary" align="center">
            💡 Tip: Open the main application in another tab to see changes in real-time
          </Typography>
          <Box display="flex" justifyContent="center" mt={2}>
            <Button onClick={() => navigate('/')}>
              Back to Application
            </Button>
          </Box>
        </Box>
      </Paper>
    </Container>
  );
}